import type { Flight, FlightLeg } from '../types';
import { searchFlights, searchReturnFlights } from './api';
import { legToStoredReturnLeg, mergeRoundTripFlight } from './roundTrip';

export type RoundTripSearchParams = {
  departureId: string;
  arrivalId: string;
  outboundDate: string;
  returnDate: string;
  adults?: number;
  bags?: number;
  stops?: '0' | '1' | '2' | '3';
  travelClass?: '1' | '2' | '3' | '4';
};

function normFlightNumber(fn: string): string {
  return fn.replace(/\s+/g, '').toUpperCase();
}

function normAirline(name: string): string {
  return name.toLowerCase().replace(/\s+/g, ' ').trim();
}

/** True when two rows are the same outbound itinerary (for matching after re-search). */
export function flightsMatchForToken(a: Flight, b: Flight): boolean {
  if (a.id === b.id) return true;
  if (a.departure !== b.departure || a.arrival !== b.arrival) return false;
  if (normFlightNumber(a.flightNumber) === normFlightNumber(b.flightNumber)) return true;
  if (normAirline(a.airline) === normAirline(b.airline) && normFlightNumber(a.flightNumber) === normFlightNumber(b.flightNumber)) {
    return true;
  }
  return a.departureTime === b.departureTime && a.arrivalTime === b.arrivalTime;
}

/** Copy departure_token from a matching row in the latest live search results. */
export function resolveOutboundWithToken(outbound: Flight, pool: Flight[]): Flight {
  if (outbound.departureToken?.trim()) return outbound;
  const match = pool.find((f) => f.departureToken?.trim() && flightsMatchForToken(f, outbound));
  if (match?.departureToken) {
    return { ...outbound, departureToken: match.departureToken };
  }
  return outbound;
}

/**
 * SerpAPI only includes departure_token on round-trip (type=1) search results.
 * Re-run search and attach the token when the outbound was picked from a one-way search or inventory.
 */
export async function attachDepartureTokenFromRoundTripSearch(
  outbound: Flight,
  search: RoundTripSearchParams,
): Promise<Flight> {
  if (outbound.departureToken?.trim()) return outbound;

  const result = await searchFlights({
    type: '1',
    departureId: search.departureId.trim().toUpperCase(),
    arrivalId: search.arrivalId.trim().toUpperCase(),
    outboundDate: search.outboundDate.trim(),
    returnDate: search.returnDate.trim(),
    adults: search.adults ?? 1,
    bags: search.bags ?? 0,
    stops: search.stops ?? '0',
    travelClass: search.travelClass ?? '1',
    deepSearch: true,
  });

  const withToken = result.flights.filter((f) => f.departureToken?.trim());
  const match =
    withToken.find((f) => f.id === outbound.id) ||
    withToken.find((f) => flightsMatchForToken(f, outbound)) ||
    withToken.find(
      (f) =>
        f.departure === outbound.departure &&
        f.arrival === outbound.arrival &&
        normFlightNumber(f.flightNumber) === normFlightNumber(outbound.flightNumber),
    );

  if (match?.departureToken) {
    return { ...outbound, departureToken: match.departureToken };
  }
  return outbound;
}

/** Copy itinerary fields from a search result into a stored return leg (no price/id). */
export function flightToLeg(f: Flight): FlightLeg {
  return {
    airline: f.airline,
    flightNumber: f.flightNumber,
    departure: f.departure,
    arrival: f.arrival,
    departureTime: f.departureTime,
    arrivalTime: f.arrivalTime,
    travelDate: f.travelDate,
    totalDuration: f.totalDuration,
    stopsCount: f.stopsCount,
    layovers: f.layovers,
    segments: f.segments,
    referencePrice: f.price > 0 ? f.price : undefined,
  };
}

/** Fetch return flights from SerpAPI when missing on a round-trip selection. */
export async function enrichFlightWithReturn(
  flight: Flight,
  opts: RoundTripSearchParams & { tripType: '1' | '2' },
): Promise<Flight> {
  const returnDate = opts.returnDate?.trim();
  const base: Flight = {
    ...flight,
    tripType: opts.tripType,
    returnDate: returnDate || flight.returnDate,
  };

  if (opts.tripType !== '1' || !returnDate || base.returnLeg) {
    return base;
  }

  if (!flight.departureToken) {
    return base;
  }

  const dep = opts.departureId.trim().toUpperCase();
  const arr = opts.arrivalId.trim().toUpperCase();
  const outDate = opts.outboundDate.trim();
  if (!dep || !arr || !outDate) {
    return base;
  }

  try {
    const result = await searchReturnFlights({
      departureToken: flight.departureToken,
      departureId: dep,
      arrivalId: arr,
      outboundDate: outDate,
      returnDate,
      type: '1',
      adults: opts.adults,
      bags: opts.bags,
      stops: opts.stops,
      travelClass: opts.travelClass,
    });
    const best = result.flights[0];
    if (!best) return base;
    return mergeRoundTripFlight(base, best, returnDate);
  } catch {
    return base;
  }
}
